// src/views/menage/MonMoisAgent.jsx — « LE MOIS » de l'agent (maquette
// validée Dimitri 2026-08-22), derrière la bascule de Mon planning.
//
//   · CINQ SEMAINES (ou six) — le mois ENTIER, jours voisins en retrait.
//   · DES ICÔNES, PAS DES CHIFFRES NUS : balai vert + nombre de ménages,
//     œil aubergine + nombre de contrôles, les heures dessous en petit gris.
//     Une LÉGENDE en pied (balai = ménages, œil = contrôles).
//   · Fonds : ménages vert pâle · ménages ET contrôles aubergine pâle ·
//     aujourd'hui bordure foncée 2 px, quoi qu'il arrive.
//   · CLIQUER UN JOUR ouvre sa liste dessous (heure, logement, nature) ;
//     cliquer une ligne ouvre SA CARTE (ménage ou contrôle), en place.
//   · « MA CHARGE » : cette semaine / semaine prochaine / tout le mois, en
//     heures — cliquer une période SURLIGNE ses jours dans le calendrier.

import { useState, useEffect, useMemo, useCallback } from 'react'
import { Brush, Eye, ChevronLeft, ChevronRight } from 'lucide-react'
import { DS } from '../../lib/ui'
import { fetchMoisAgent } from '../../lib/agent-journee-api'
import { SANS_NOM } from '../../lib/agent-journee-regles'
import { fetchControlesMois } from '../../lib/controle-agent-api'
import { fetchLogInfo } from '../../lib/menage-log-info-cache'
import { dureeDuRunAgent } from '../../lib/menage-journee-agent'
import CarteMenageAgent from './CarteMenageAgent'
import { NEST, POLICE } from './palette-nest'

// Vert : ses ménages ; aubergine : ménages et contrôles. Aucun bleu (maquette).
const VERT = { fond: NEST.vertLigne, bord: NEST.vertBord, ico: NEST.vert }
const AMBRE = { fond: NEST.aubPale, bord: NEST.aubBord, ico: NEST.aub }
const NAVY = NEST.encre

function moisCourantParis() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date()).slice(0, 7)
}
function aujourdHuiParis() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date())
}
function libelleMoisFR(month) {
  const t = Date.parse(`${month}-01T12:00:00Z`)
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'Europe/Paris' }).format(t)
}
function moisVoisin(month, pas) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + pas, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}
// « 21 août » — la règle des dates.
function dateCourteFR(iso) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(t)
}
// Les semaines PLEINES couvrant le mois (lundi → dimanche).
export function semainesDuMois(month) {
  const [y, m] = month.split('-').map(Number)
  const premier = new Date(Date.UTC(y, m - 1, 1))
  const debut = new Date(premier)
  debut.setUTCDate(1 - ((premier.getUTCDay() + 6) % 7)) // recule au lundi
  const semaines = []
  const d = new Date(debut)
  while (true) {
    const semaine = []
    for (let i = 0; i < 7; i++) {
      semaine.push(d.toISOString().slice(0, 10))
      d.setUTCDate(d.getUTCDate() + 1)
    }
    semaines.push(semaine)
    if (d.getUTCMonth() + 1 !== m && d.getUTCFullYear() * 100 + d.getUTCMonth() + 1 > y * 100 + m) break
    if (semaines.length > 6) break
  }
  return semaines
}
function fmtHeures(min) {
  if (!min) return '0 h'
  const h = min / 60
  return Number.isInteger(h) ? `${h} h` : `${h.toFixed(1).replace('.', ',')} h`
}
// Le lundi de la semaine d'un jour ISO.
function lundiDe(iso) {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}
function plusJours(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

// Les repères de changement (maquette, x124) : sur la case et dans le jour ouvert.
const REPERES = {
  retrait: { mot: 'retiré', phrase: 'ce ménage n’est plus à vous' },
  ajout: { mot: 'nouveau', phrase: 'ajouté à votre planning' },
  heure: { mot: 'horaire modifié', phrase: 'l’heure a changé' },
  date: { mot: 'déplacé', phrase: 'déplacé à cette date' },
}

export default function MonMoisAgent({ currentUser, estControleur, onChecklist, onIncident, changements = [] }) {
  const [mois, setMois] = useState(moisCourantParis()) // jamais mémorisé
  const [runs, setRuns] = useState([])
  const [erreur, setErreur] = useState(false)
  const [controlesJours, setControlesJours] = useState({})
  const [jourOuvert, setJourOuvert] = useState(null)
  const [ligneOuverte, setLigneOuverte] = useState(null) // run_id | null
  const [infos, setInfos] = useState({})
  const [periode, setPeriode] = useState(null) // 'semaine' | 'prochaine' | 'mois' | null
  const auj = aujourdHuiParis()

  useEffect(() => {
    let annule = false
    ;(async () => {
      try {
        const lu = await fetchMoisAgent(mois)
        if (!annule) { setRuns(lu.runs); setErreur(false) }
      } catch (e) {
        console.error('[mon-mois] lecture', e?.message || 'erreur')
        if (!annule) { setRuns([]); setErreur(true) }
      }
      if (estControleur) {
        const rep = await fetchControlesMois(mois).catch(() => null)
        if (!annule) setControlesJours(rep?.jours || {})
      } else if (!annule) setControlesJours({})
    })()
    return () => { annule = true }
  }, [mois, currentUser?.email, estControleur])

  // Les infos logement du jour ouvert (adresse, code, checklist) — au clic.
  useEffect(() => {
    if (!jourOuvert) return undefined
    let annule = false
    const duJour = runs.filter((r) => r.scheduled_date === jourOuvert)
    ;(async () => {
      const paires = await Promise.all(duJour.map(async (r) => {
        try { return [`${r.logement_id}:${r.scheduled_date}`, await fetchLogInfo(r.logement_id, r.scheduled_date)] }
        catch (_) { return null }
      }))
      if (!annule) setInfos((prev) => ({ ...prev, ...Object.fromEntries(paires.filter(Boolean)) }))
    })()
    return () => { annule = true }
  }, [jourOuvert, runs])

  const parJour = useMemo(() => {
    const map = {}
    for (const r of runs) {
      const j = map[r.scheduled_date] || (map[r.scheduled_date] = { n: 0, minutes: 0 })
      j.n += 1
      j.minutes += dureeDuRunAgent(r, null) || 90
    }
    return map
  }, [runs])

  const semaines = useMemo(() => semainesDuMois(mois), [mois])
  const changementsParJour = useMemo(() => {
    const map = {}
    for (const c of changements) if (c?.date) (map[c.date] = map[c.date] || []).push(c)
    return map
  }, [changements])

  // « MA CHARGE » — les heures de SES ménages, par période.
  const charge = useMemo(() => {
    const lundiCourant = lundiDe(auj)
    const bornes = {
      semaine: [lundiCourant, plusJours(lundiCourant, 6)],
      prochaine: [plusJours(lundiCourant, 7), plusJours(lundiCourant, 13)],
      mois: [`${mois}-01`, `${mois}-31`],
    }
    const somme = ([a, b]) => Object.entries(parJour)
      .filter(([j]) => j >= a && j <= b)
      .reduce((s, [, v]) => s + v.minutes, 0)
    // ⚠️ LES CONTRÔLES COMPTENT AUSSI (Dimitri 2026-08-23) — mais on
    // n'INVENTE PAS leur durée : aucun contrôle n'a de temps configuré. On
    // affiche donc leur NOMBRE à côté des heures de ménage, plutôt qu'un total
    // qui ferait passer une estimation pour une mesure.
    const compterControles = ([a, b]) => Object.entries(controlesJours)
      .filter(([j]) => j >= a && j <= b)
      .reduce((s, [, n]) => s + (Number(n) || 0), 0)
    // ⚠️ UN ZÉRO N'EST PAS « RIEN » QUAND LA DONNÉE N'EXISTE PAS ENCORE
    // (règle Dimitri, la même que la garde du héro propriétaire) : les ménages
    // sont créés LA VEILLE pour le lendemain. « 0 h » sur une période encore à
    // naître se lit « je n'ai pas de travail » — c'est faux, et démoralisant.
    // On distingue donc « rien de prévu » de « pas encore connu » : au-delà de
    // demain, tant qu'aucun ménage n'existe, on le DIT.
    const demain = plusJours(auj, 1)
    const pasEncore = (bornes2, min) => min === 0 && bornes2[0] > demain
    const mSemaine = somme(bornes.semaine)
    const mProchaine = somme(bornes.prochaine)
    const mMois = somme(bornes.mois)
    const cSemaine = compterControles(bornes.semaine)
    const cProchaine = compterControles(bornes.prochaine)
    const cMois = compterControles(bornes.mois)
    return {
      semaine: { libelle: 'Cette semaine', minutes: mSemaine, controles: cSemaine, bornes: bornes.semaine,
        inconnu: pasEncore(bornes.semaine, mSemaine + cSemaine) },
      prochaine: { libelle: 'Semaine prochaine', minutes: mProchaine, controles: cProchaine, bornes: bornes.prochaine,
        inconnu: pasEncore(bornes.prochaine, mProchaine + cProchaine) },
      mois: { libelle: 'Tout le mois', minutes: mMois, controles: cMois, bornes: bornes.mois,
        inconnu: pasEncore(bornes.mois, mMois + cMois) },
    }
  }, [parJour, controlesJours, auj, mois])

  const dansPeriode = useCallback((iso) => {
    if (!periode) return false
    const [a, b] = charge[periode].bornes
    return iso >= a && iso <= b && !!parJour[iso]
  }, [periode, charge, parJour])

  const ouvrirJour = (iso) => {
    setLigneOuverte(null)
    setJourOuvert((j) => (j === iso ? null : iso))
  }

  const duJour = jourOuvert ? runs.filter((r) => r.scheduled_date === jourOuvert) : []
  const nbControlesJour = jourOuvert ? (controlesJours[jourOuvert] || 0) : 0

  return (
    <div data-testid="mon-mois" style={{ fontFamily: POLICE }}>
      {/* ── Le mois + flèches. ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 10px' }}>
        <button type="button" data-testid="mois-prec" aria-label="Mois précédent"
          onClick={() => { setMois((m) => moisVoisin(m, -1)); setJourOuvert(null) }} className="fbz-press"
          style={{ width: 44, height: 44, borderRadius: 10, border: `1px solid ${DS.border}`,
            background: '#fff', color: NAVY, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center' }}>
          <ChevronLeft size={17} strokeWidth={2.2} />
        </button>
        <div data-testid="mois-libelle" style={{ flex: 1, textAlign: 'center', fontFamily: POLICE,
          fontSize: 17, fontWeight: 600, color: NAVY, textTransform: 'capitalize' }}>
          {libelleMoisFR(mois)}
        </div>
        <button type="button" data-testid="mois-suiv" aria-label="Mois suivant"
          onClick={() => { setMois((m) => moisVoisin(m, 1)); setJourOuvert(null) }} className="fbz-press"
          style={{ width: 44, height: 44, borderRadius: 10, border: `1px solid ${DS.border}`,
            background: '#fff', color: NAVY, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center' }}>
          <ChevronRight size={17} strokeWidth={2.2} />
        </button>
      </div>

      {erreur && (
        <p data-testid="mois-erreur" style={{ fontSize: 12.5, color: NEST.aubTexte, margin: '0 2px 10px' }}>
          La lecture de ce mois n'a pas abouti. Vérifiez votre connexion.
        </p>
      )}

      {/* ── La grille — semaines PLEINES, jours voisins en retrait. ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 4 }}>
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((l, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 700,
            color: '#98a2ad', textTransform: 'uppercase', padding: '2px 0' }}>{l}</div>
        ))}
      </div>
      <div data-testid="mois-grille" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {semaines.flat().map((iso) => {
          const duMois = iso.slice(0, 7) === mois
          const j = parJour[iso]
          const nc = controlesJours[iso] || 0
          const deux = j && nc > 0
          const reperes = changementsParJour[iso] || []
          const fond = !duMois ? 'transparent' : deux ? AMBRE.fond : j ? VERT.fond : nc > 0 ? AMBRE.fond : '#fff'
          const bord = iso === auj ? `2px solid ${NAVY}`
            : !duMois ? '1px solid transparent'
            : deux || (!j && nc > 0) ? `1px solid ${AMBRE.bord}` : j ? `1px solid ${VERT.bord}` : `1px solid ${DS.border}`
          return (
            <button key={iso} type="button" data-testid={`jour-${iso}`}
              onClick={() => duMois && (j || nc > 0 || reperes.length > 0) && ouvrirJour(iso)}
              className={duMois && (j || nc || reperes.length) ? 'fbz-press' : undefined}
              style={{ minHeight: 58, borderRadius: 9, border: bord, background: fond,
                opacity: duMois ? 1 : 0.38, padding: '4px 2px', cursor: duMois && (j || nc || reperes.length) ? 'pointer' : 'default',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                outline: dansPeriode(iso) ? `2px solid ${NEST.vert}` : 'none', outlineOffset: -1,
                fontFamily: POLICE }}>
              <span style={{ fontSize: 10.5, fontWeight: 700,
                color: iso === auj ? NAVY : duMois ? '#5c6773' : '#b4bcc5' }}>
                {Number(iso.slice(8, 10))}
              </span>
              {(j || nc > 0) && duMois && (
                <span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  {j && (
                    <span data-testid={`jour-menages-${iso}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <Brush size={11} strokeWidth={2.4} color={VERT.ico} />
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: VERT.ico }}>{j.n}</span>
                    </span>
                  )}
                  {nc > 0 && (
                    <span data-testid={`jour-controles-${iso}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <Eye size={11} strokeWidth={2.4} color={AMBRE.ico} />
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: AMBRE.ico }}>{nc}</span>
                    </span>
                  )}
                </span>
              )}
              {j && duMois && (
                <span style={{ fontSize: 8.5, color: '#98a2ad' }}>{fmtHeures(j.minutes)}</span>
              )}
              {duMois && reperes.slice(0, 1).map((c) => (
                <span key={c.id} data-testid={`jour-repere-${iso}`} style={{ fontSize: 9, fontWeight: 700, borderRadius: 5, padding: '0 4px', lineHeight: 1.5,
                  background: c.type === 'retrait' ? NEST.aubPale : NEST.vertPale, color: c.type === 'retrait' ? NEST.aubTexte : NEST.vertFonce }}>
                  {REPERES[c.type]?.mot || 'changement'}
                </span>
              ))}
            </button>
          )
        })}
      </div>
      {/* ⚠️ LA LÉGENDE — les icônes se comprennent sans lire, la légende confirme. */}
      <div data-testid="mois-legende" style={{ display: 'flex', gap: 14, justifyContent: 'center',
        marginTop: 7, fontSize: 10.5, color: '#6b7683' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Brush size={11} strokeWidth={2.4} color={VERT.ico} /> ménages
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Eye size={11} strokeWidth={2.4} color={AMBRE.ico} /> contrôles
        </span>
      </div>

      {/* ── LE JOUR OUVERT — la liste dessous ; une ligne ouvre SA carte. ── */}
      {jourOuvert && (
        <div data-testid="jour-detail" style={{ marginTop: 12, background: '#fff',
          border: `1px solid ${DS.border}`, borderRadius: 12, padding: '12px 13px' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: NAVY, marginBottom: 8 }}>
            {dateCourteFR(jourOuvert)}
          </div>
          {(changementsParJour[jourOuvert] || []).map((c) => {
            const r = REPERES[c.type] || { mot: 'changement', phrase: '' }
            return (
              <div key={c.id} data-testid={`jour-changement-${c.id}`} style={{ fontSize: 12.5, marginBottom: 6, borderRadius: 8, padding: '6px 9px',
                background: c.type === 'retrait' ? NEST.aubPale : NEST.vertPale, color: c.type === 'retrait' ? NEST.aubTexte : NEST.vertFonce }}>
                <b>{r.mot.charAt(0).toUpperCase() + r.mot.slice(1)}</b> · {c.logement?.nom || SANS_NOM}{r.phrase ? ` — ${r.phrase}` : ''}
              </div>
            )
          })}
          {duJour.map((r) => {
            const info = infos[`${r.logement_id}:${r.scheduled_date}`] || null
            const ouverte = ligneOuverte === r.id
            return (
              <div key={r.id}>
                <button type="button" data-testid={`jour-ligne-${r.id}`}
                  onClick={() => setLigneOuverte((l) => (l === r.id ? null : r.id))}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                    minHeight: 44, background: 'none', border: 'none', padding: '4px 0',
                    borderBottom: `1px solid ${DS.border}33`, cursor: 'pointer',
                    textAlign: 'left', fontFamily: POLICE }}>
                  <Brush size={13} strokeWidth={2.4} color={VERT.ico} style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#233240', flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums' }}>
                    {r.heure_fixee ? r.heure_fixee.slice(0, 5).replace(':', 'h').replace(/^0/, '') : '—'}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#4a5866',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {info?.nom || r.logement_nom || 'un logement'}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 10.5, color: '#98a2ad' }}>ménage</span>
                </button>
                {ouverte && (
                  <div style={{ margin: '8px 0' }}>
                    <CarteMenageAgent run={r} index={null} info={info}
                      indisponible={r.scheduled_date > auj}
                      onCommencer={() => onChecklist && onChecklist(r)}
                      onIncident={() => onIncident && onIncident(r)} />
                  </div>
                )}
              </div>
            )
          })}
          {nbControlesJour > 0 && (
            <div data-testid="jour-controles-note" style={{ display: 'flex', gap: 8,
              alignItems: 'center', padding: '8px 0', fontSize: 12, color: AMBRE.ico }}>
              <Eye size={13} strokeWidth={2.4} />
              {nbControlesJour} contrôle{nbControlesJour > 1 ? 's' : ''} à faire ce jour-là — le détail vit dans la journée.
            </div>
          )}
          {duJour.length === 0 && nbControlesJour === 0 && !(changementsParJour[jourOuvert] || []).length && (
            <div style={{ fontSize: 12, color: DS.textMuted }}>Rien ce jour-là.</div>
          )}
        </div>
      )}

      {/* ── « MA CHARGE » — cliquer une période surligne ses jours. ── */}
      <div data-testid="ma-charge" style={{ marginTop: 12, background: '#fff',
        border: `1px solid ${DS.border}`, borderRadius: 12, padding: '12px 13px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#98a2ad', textTransform: 'uppercase',
          letterSpacing: 0.5, marginBottom: 8 }}>Ma charge</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {Object.entries(charge).map(([cle, c]) => {
            const actif = periode === cle
            return (
              <button key={cle} type="button" data-testid={`charge-${cle}`}
                aria-pressed={actif}
                onClick={() => setPeriode((p) => (p === cle ? null : cle))} className="fbz-press"
                style={{ minHeight: 52, borderRadius: 9, padding: '7px 6px',
                  border: actif ? `2px solid ${NEST.vert}` : `1px solid ${NEST.bord}`,
                  background: actif ? NEST.vertPale : NEST.fondCarte, cursor: 'pointer',
                  fontFamily: POLICE }}>
                {/* « pas encore connu » plutôt qu'un zéro qui ment. */}
                <span style={{ display: 'block',
                  fontSize: c.inconnu && !erreur ? 10.5 : 14, fontWeight: 700,
                  color: c.inconnu ? '#98a2ad' : NAVY,
                  fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                  {erreur ? '—' : c.inconnu ? 'pas encore connu' : fmtHeures(c.minutes)}
                </span>
                {c.controles > 0 && (
                  <span data-testid={`charge-controles-${cle}`}
                    style={{ display: 'block', fontSize: 9.5, color: NEST.aubTexte, marginTop: 1 }}>
                    + {c.controles} contrôle{c.controles > 1 ? 's' : ''}
                  </span>
                )}
                <span style={{ display: 'block', fontSize: 10, color: '#6b7683', marginTop: 1 }}>{c.libelle}</span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
